import { ValueProvenance } from '@edgebox/data-definition-kit';
import {
  ContentRecommendationStatus,
  SyncCoreDataDefinitionsEnumTranslator,
  TaxonomyFacet,
  TaxonomyTermPriority,
} from '@edgebox/sync-core-data-definitions';
import { Alert, Button } from '@edgebox/react-components';
import { facetDisplayName } from '../taxonomyFacetMetadata';
import { purposePriorityRank } from './purposeTabs.utils';
import type { SuggestionRevisionReference } from './suggestedFaqs.utils';
import type { RuntimeDate } from '@edgebox/data-definition-kit';
import type { ClientContentRecommendationEntity, IClientContentRecommendationText } from '@edgebox/sync-core-rest-client';
import React, { useCallback, useMemo, useState } from 'react';

/**
 * The Suggested FAQs section of a page's FAQ tab: the FAQs suggested for this
 * page, each with what it serves, how much to trust it and why it is on this
 * page, plus the two actions.
 *
 * Every value rendered here comes from the suggestion as the REST client's own
 * entity carries it: a reference answers its key and its revision through its
 * own calls, and a stamp is a moment. A field that is absent renders no badge and no clause, and no row ever
 * shows an empty label or a raw taxonomy key: a term that has not resolved to a
 * name is left out entirely.
 *
 * The score and its three halves, the attribute checklist, any person's name,
 * any check name, the suggestion's key, its evaluation and resolution stamps,
 * its cluster keys and every target row other than this page's are deliberately
 * not rendered. The score is normalised per computation and means nothing across
 * batches; the Priority badge is the only importance shown and the demand counts
 * the only comparable numbers.
 */

/**
 * A reference resolved to the name the tab shows for it: a taxonomy term with
 * the facet that places it, a page with the address it lives at, or another
 * suggestion named by its question.
 */
export interface IResolvedSuggestionTerm {
  name: string;
  facet?: TaxonomyFacet;
  /** The address a page lives at. A taxonomy term and a suggestion have none. */
  url?: string;
}

/**
 * Reads an already-loaded term. It answers `undefined` while the term has not
 * resolved, which is what keeps a raw key out of the badge row.
 */
export type SuggestedFaqTermResolver = (termKey: string) => IResolvedSuggestionTerm | undefined;

/** What a row shows in place of its two actions once the suggestion has moved on. */
export type SuggestedFaqRowState = 'open' | 'added' | 'dismissed' | 'not-a-suggestion';

/**
 * Where the draft a row asked for stands. `written` means the site has it, and
 * the Optimization tab then carries it; the others say what is true while it is
 * not there yet.
 */
export type SuggestedFaqDraftState = 'generating' | 'slow' | 'unreadable' | 'failed' | 'written';

const DRAFT_STATE_NOTES: Record<Exclude<SuggestedFaqDraftState, 'written'>, string> = {
  generating: 'The draft is being written and will appear on the Optimization tab.',
  slow: 'The draft is taking longer than usual; it will appear on the Optimization tab when it is ready.',
  unreadable: "The draft's state couldn't be read; it will appear on the Optimization tab when it is ready.",
  failed: "The draft couldn't be written.",
};

export const SUGGESTED_FAQ_SKELETON_COUNT = 2;

const ROW_STATE_LABELS: Record<Exclude<SuggestedFaqRowState, 'open'>, string> = {
  added: 'Added',
  dismissed: 'Dismissed',
  'not-a-suggestion': 'Not a suggestion',
};

/**
 * Reads a label out of the definitions package rather than a table kept here:
 * the product's word for a value lives with the value. The domain package and
 * the generated client declare these enums under member names that diverge
 * wherever a value starts with a digit, so the wire value is what is looked up.
 * A member the package does not translate renders nothing at all.
 */
function translateEnumValue(
  enumName: 'TaxonomyTermPriority' | 'ContentRecommendationTargetState' | 'ContentRecommendationLinkTier' | 'ValueProvenance',
  value: string | null | undefined
): string | undefined {
  if (!value) {
    return undefined;
  }

  return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue(enumName, value)?.trim() || undefined;
}

/** Most urgent first: by priority, then by when it was last seen, newest first. */
export function compareSuggestedFaqs(left: ClientContentRecommendationEntity, right: ClientContentRecommendationEntity): number {
  // The priority values carry their order in their own numeric prefix, so the
  // prefix is read rather than a ranking table maintained beside the enum.
  const rankDifference =
    purposePriorityRank(right.priority as TaxonomyTermPriority | undefined) -
    purposePriorityRank(left.priority as TaxonomyTermPriority | undefined);
  if (rankDifference !== 0) {
    return rankDifference;
  }

  return epochOf(right.lastDetectedAt) - epochOf(left.lastDetectedAt);
}

export function sortSuggestedFaqs(suggestions: ClientContentRecommendationEntity[]): ClientContentRecommendationEntity[] {
  return [...suggestions].sort(compareSuggestedFaqs);
}

function findPageTarget(suggestion: ClientContentRecommendationEntity, pageKey: string) {
  return suggestion.targets?.find((target) => target.getStableKey() === pageKey);
}

function firstText(suggestion: ClientContentRecommendationEntity): IClientContentRecommendationText | undefined {
  return suggestion.texts?.[0];
}

/** A stamp as a number, so two suggestions can be ordered by when they were last seen. */
function epochOf(at: RuntimeDate | null | undefined): number {
  return at?.isValid() ? at.valueOf() : 0;
}

function formatDay(at: RuntimeDate | null | undefined): string | undefined {
  if (!at?.isValid()) {
    return undefined;
  }

  return at.toDate().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Where a text's wording came from, as the definitions package words it. Every
 * member the package translates renders its own label, so a value the entity can
 * carry beside the two commonest ones is never shown as one of them. No person
 * is ever named on this tab.
 */
function formatSource(provenance: ValueProvenance | null | undefined): string | undefined {
  return translateEnumValue('ValueProvenance', provenance);
}

/**
 * Turns a rate over a denominator back into the count it was taken from. The two
 * numbers are named rather than positional, so a caller cannot swap them and
 * silently change what the demand clause states.
 */
function countFromRate({
  rate,
  denominator,
}: {
  rate: number | null | undefined;
  denominator: number | null | undefined;
}): number | undefined {
  if (typeof rate !== 'number' || typeof denominator !== 'number' || !Number.isFinite(rate) || !Number.isFinite(denominator)) {
    return undefined;
  }

  return Math.round(rate * denominator);
}

/**
 * The state a row shows in place of its actions, from the suggestion's own
 * status. The list this tab reads asks for the open ones, so a settled status
 * arrives only when Sync Core answers one anyway; the mapping is the entity's,
 * not the query's.
 */
export function toSettledRowState(status: string | null | undefined): SuggestedFaqRowState {
  if (status === ContentRecommendationStatus.Fixed) {
    return 'added';
  }

  if (status === ContentRecommendationStatus.Ignored) {
    return 'dismissed';
  }

  if (status === ContentRecommendationStatus.FalsePositive) {
    return 'not-a-suggestion';
  }

  return 'open';
}

function resolveTermName(
  resolveTerm: SuggestedFaqTermResolver,
  reference: SuggestionRevisionReference | null | undefined
): string | undefined {
  const key = reference?.getStableKey();
  if (!key) {
    return undefined;
  }

  const resolved = resolveTerm(key);
  return resolved?.name?.trim() || undefined;
}

function resolveTermNames(
  resolveTerm: SuggestedFaqTermResolver,
  references: Array<SuggestionRevisionReference> | null | undefined
): string[] {
  return (references ?? []).map((reference) => resolveTermName(resolveTerm, reference)).filter((name): name is string => !!name);
}

/**
 * The Authority topic badge needs the term's facet as well as its name, so it
 * is the entry in `terms` whose resolved facet is the authority-topic facet.
 */
function resolveAuthorityTopic(suggestion: ClientContentRecommendationEntity, resolveTerm: SuggestedFaqTermResolver): string | undefined {
  for (const reference of suggestion.terms ?? []) {
    const key = reference?.getStableKey();
    if (!key) {
      continue;
    }

    const resolved = resolveTerm(key);
    if (resolved?.facet === TaxonomyFacet.AuthorityTopic && resolved.name.trim()) {
      return resolved.name.trim();
    }
  }

  return undefined;
}

function SuggestedFaqBadge(props: { label: string; value: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs">
      <span className="font-semibold text-slate-500">{props.label}:</span>
      <span className="text-slate-700">{props.value}</span>
    </span>
  );
}

/** The badge row, each badge carrying its own label, in the order the tab states. */
export function SuggestedFaqBadgeRow(props: {
  suggestion: ClientContentRecommendationEntity;
  pageKey: string;
  resolveTerm: SuggestedFaqTermResolver;
}) {
  const { suggestion, pageKey, resolveTerm } = props;
  const target = findPageTarget(suggestion, pageKey);

  const badges: Array<{ label: string; value: string }> = [];

  const thisPage = translateEnumValue('ContentRecommendationTargetState', target?.metadata?.state);
  if (thisPage) {
    badges.push({ label: 'This page', value: thisPage });
  }

  const priority = translateEnumValue('TaxonomyTermPriority', suggestion.priority);
  if (priority) {
    badges.push({ label: 'Priority', value: priority });
  }

  const angle = resolveTermName(resolveTerm, suggestion.angle);
  if (angle) {
    badges.push({ label: 'Angle', value: angle });
  }

  const authorityTopic = resolveAuthorityTopic(suggestion, resolveTerm);
  if (authorityTopic) {
    badges.push({ label: facetDisplayName(TaxonomyFacet.AuthorityTopic), value: authorityTopic });
  }

  const subject = resolveTermName(resolveTerm, suggestion.subject);
  if (subject) {
    badges.push({ label: 'Subject', value: subject });
  }

  const faqType = resolveTermName(resolveTerm, suggestion.faqType);
  if (faqType) {
    badges.push({ label: 'FAQ type', value: faqType });
  }

  const initiative = resolveTermName(resolveTerm, suggestion.initiative);
  if (initiative) {
    badges.push({ label: 'Initiative', value: initiative });
  }

  if (!badges.length) {
    return null;
  }

  return (
    <ul data-testid="suggested-faq-badges" className="mt-2 flex list-none flex-wrap items-center gap-2 p-0">
      {badges.map((badge) => (
        <li key={`${badge.label}:${badge.value}`}>
          <SuggestedFaqBadge label={badge.label} value={badge.value} />
        </li>
      ))}
    </ul>
  );
}

/** Builds the details line, clause by clause; a clause whose field is absent is left out. */
export function buildSuggestedFaqDetailClauses(
  suggestion: ClientContentRecommendationEntity,
  pageKey: string,
  resolveTerm: SuggestedFaqTermResolver
): string[] {
  const clauses: string[] = [];
  const target = findPageTarget(suggestion, pageKey);
  const text = firstText(suggestion);

  const reason = suggestion.publishOn?.reason?.trim();
  if (reason) {
    clauses.push(reason);
  }

  const tie = translateEnumValue('ContentRecommendationLinkTier', target?.metadata?.linkTier);
  if (tie) {
    clauses.push(`Tied to this page: ${tie}`);
  }

  const evidence = suggestion.evidence;
  if (evidence) {
    const unprompted = countFromRate({ rate: evidence.unpromptedRate, denominator: evidence.unpromptedDenominator });
    if (typeof unprompted === 'number') {
      clauses.push(`Raised unprompted in ${unprompted} of ${evidence.unpromptedDenominator} answers`);

      const gap = countFromRate({ rate: evidence.gapRate, denominator: evidence.gapDenominator });
      if (typeof gap === 'number') {
        clauses.push(`${gap} of those ${evidence.gapDenominator} cite nothing you own`);
      }
    }

    const seen = (evidence.coverage ?? [])
      .map((entry) => {
        const engine = resolveTerm(entry.engine?.getStableKey() ?? '')?.name?.trim();
        if (!engine) {
          return undefined;
        }

        const stage = resolveTerm(entry.purpose?.getStableKey() ?? '')?.name?.trim();
        const answers = typeof entry.answers === 'number' ? `${entry.answers} answers` : undefined;
        return [engine, stage, answers].filter((part): part is string => !!part).join(', ');
      })
      .filter((entry): entry is string => !!entry);
    if (seen.length) {
      clauses.push(`Seen on ${seen.join('; ')}`);
    }

    // Sync Core resolves the authority sources most cited first, so their own
    // order is the order shown.
    const sources = resolveTermNames(resolveTerm, evidence.gapSources);
    if (sources.length) {
      clauses.push(`Answered today by ${sources.join(', ')}`);
    }
  }

  const firstDetected = formatDay(suggestion.firstDetectedAt);
  const lastDetected = formatDay(suggestion.lastDetectedAt);
  if (firstDetected && lastDetected) {
    clauses.push(`Detected ${firstDetected}, last seen ${lastDetected}`);
  } else if (firstDetected) {
    clauses.push(`Detected ${firstDetected}`);
  }

  const questionSource = formatSource(text?.title?.provenance);
  const questionAt = formatDay(text?.title?.at);
  if (questionSource) {
    clauses.push(`Question source: ${questionSource}${questionAt ? `, written ${questionAt}` : ''}`);
  }

  const answerSource = formatSource(text?.content?.provenance);
  const answerAt = formatDay(text?.content?.at);
  if (answerSource) {
    clauses.push(`Example answer source: ${answerSource}${answerAt ? `, written ${answerAt}` : ''}`);
  }

  const framing = text?.brief?.text?.trim();
  if (framing) {
    clauses.push(`How to frame it: ${framing}`);
  }

  const publishOnPage = resolveTermName(resolveTerm, suggestion.publishOn?.contentItem);
  if (publishOnPage) {
    clauses.push(`Answer it from ${publishOnPage}`);
  }

  const dependsOn = resolveTermNames(resolveTerm, suggestion.dependsOn);
  if (dependsOn.length) {
    clauses.push(`Waits on ${dependsOn.join(', ')}`);
  }

  return clauses;
}

function SuggestedFaqEvidenceDisclosure(props: { suggestion: ClientContentRecommendationEntity; pageKey: string }) {
  const target = findPageTarget(props.suggestion, props.pageKey);
  const reasons = (target?.metadata?.evidence ?? [])
    .map((entry) => entry?.reasoning?.trim())
    .filter((reasoning): reasoning is string => !!reasoning);

  if (!reasons.length) {
    return null;
  }

  return (
    <details className="mt-2 text-sm text-slate-600">
      <summary className="cursor-pointer font-semibold text-slate-700">Show evidence</summary>
      <ul className="mt-2 list-disc space-y-1 pl-5">
        {reasons.map((reasoning, index) => (
          <li key={`${index}:${reasoning}`}>{reasoning}</li>
        ))}
      </ul>
    </details>
  );
}

/**
 * One of the customer's own pages an answer can be sourced from, ready to
 * render: the resolved name and address, under the stable key that resolved
 * them.
 */
interface IResolvedSuggestionSource extends Pick<IResolvedSuggestionTerm, 'name' | 'url'> {
  key: string;
}

/**
 * The customer's own pages this FAQ can be answered from, each named by the page
 * and addressed by the page's own URL, so a writer opens the page instead of
 * hunting for it. A page opens in a tab of its own: this screen renders inside
 * the CMS's frame, and following a link in place would carry the frame away
 * from the tab the reader is working in.
 *
 * A page whose name has not resolved yet is left out, the way an unresolved
 * badge is, and a page the record holds no address for is named without being
 * linked — the address travels only where the page has one.
 */
export function SuggestedFaqSources(props: { suggestion: ClientContentRecommendationEntity; resolveTerm: SuggestedFaqTermResolver }) {
  const { suggestion, resolveTerm } = props;

  const pages = useMemo(
    () =>
      (suggestion.sources ?? [])
        .map((reference): IResolvedSuggestionSource | undefined => {
          const key = reference?.getStableKey();
          if (!key) {
            return undefined;
          }

          const resolved = resolveTerm(key);
          const name = resolved?.name?.trim();
          return name ? { key, name, url: resolved?.url?.trim() || undefined } : undefined;
        })
        .filter((page): page is IResolvedSuggestionSource => !!page),
    [suggestion, resolveTerm]
  );

  if (!suggestion.sources?.length) {
    return <p className="mt-3 text-xs text-slate-500">No pages found to source the answer from.</p>;
  }

  if (!pages.length) {
    return null;
  }

  return (
    <div className="mt-3 text-xs text-slate-500">
      {/* Where the answer is sourced FROM. The details line separately names the
          page the answer is published ON, so this one says which of the two it is
          rather than repeating that clause's opening words. */}
      <span className="font-semibold">Source the answer from</span>
      <ul data-testid="suggested-faq-sources" className="mt-1 flex list-none flex-wrap items-center gap-x-3 gap-y-1 p-0">
        {pages.map((page, index) => (
          <li key={`${index}:${page.key}`}>
            {page.url ? (
              <a href={page.url} target="_blank" rel="noopener noreferrer" className="text-primary font-bold hover:underline">
                {page.name}
              </a>
            ) : (
              <span className="text-slate-700">{page.name}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface ISuggestedFaqRowProps {
  suggestion: ClientContentRecommendationEntity;
  pageKey: string;
  resolveTerm: SuggestedFaqTermResolver;
  state: SuggestedFaqRowState;
  /**
   * Moves to the Optimization tab of this same page. The tab bar is not on the
   * route, and this tab adds none, so the added state offers a control rather
   * than an address.
   */
  onOpenOptimizationTab: () => void;
  /**
   * Whether that tab is on the bar. A draft the site has not written yet has no
   * Optimization tab, and offering a control that lands the reader somewhere
   * else is worse than offering none.
   */
  canOpenOptimizationTab: boolean;
  /** Where the draft this row asked for stands. */
  draftState?: SuggestedFaqDraftState;
  onAdd: (suggestion: ClientContentRecommendationEntity) => Promise<void>;
  onDismiss: (suggestion: ClientContentRecommendationEntity) => Promise<void>;
  announce: (message: string) => void;
}

export function SuggestedFaqRow(props: ISuggestedFaqRowProps) {
  const { suggestion, pageKey, resolveTerm, state, onOpenOptimizationTab, canOpenOptimizationTab, draftState, onAdd, onDismiss, announce } =
    props;
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const text = firstText(suggestion);
  const question = text?.title?.text?.trim();
  const exampleAnswer = text?.content?.text?.trim();
  const clauses = useMemo(() => buildSuggestedFaqDetailClauses(suggestion, pageKey, resolveTerm), [suggestion, pageKey, resolveTerm]);

  const run = useCallback(
    async (action: (subject: ClientContentRecommendationEntity) => Promise<void>, announcement: string, failure: string) => {
      setBusy(true);
      setActionError(undefined);
      try {
        await action(suggestion);
        announce(announcement);
      } catch {
        // The generated client reports its own internal wording for every
        // failure, so the row states the failure in the tab's own words.
        setActionError(failure);
      } finally {
        setBusy(false);
      }
    },
    [announce, suggestion]
  );

  const settledLabel = state === 'open' ? undefined : ROW_STATE_LABELS[state];

  return (
    <li className="rounded-xl p-4 ring-1 ring-inset ring-slate-200/80">
      {question ? <h4 className="text-sm font-semibold text-slate-900">{question}</h4> : null}
      <SuggestedFaqBadgeRow suggestion={suggestion} pageKey={pageKey} resolveTerm={resolveTerm} />
      {exampleAnswer ? <p className="mt-3 whitespace-pre-line text-sm text-slate-700">{exampleAnswer}</p> : null}
      <SuggestedFaqSources suggestion={suggestion} resolveTerm={resolveTerm} />
      {clauses.length ? <p className="mt-2 text-xs text-slate-500">{clauses.join(' · ')}</p> : null}
      <SuggestedFaqEvidenceDisclosure suggestion={suggestion} pageKey={pageKey} />

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {/* The Optimization affordance moves to a tab rather than to an address, so it stays a button:
            the link variant of the design system's button renders an anchor. */}
        {settledLabel ? (
          <>
            <span className="text-sm font-semibold text-slate-700">{settledLabel}</span>
            {state === 'added' && canOpenOptimizationTab ? (
              <Button variant="text-primary" size="sm" onClick={onOpenOptimizationTab}>
                Optimization
              </Button>
            ) : draftState && draftState !== 'written' ? (
              <span className={draftState === 'failed' ? 'text-sm text-rose-700' : 'text-sm text-slate-500'}>
                {DRAFT_STATE_NOTES[draftState]}
              </span>
            ) : null}
          </>
        ) : (
          <>
            <Button variant="primary" disabled={busy} onClick={() => void run(onAdd, 'Added', "Couldn't add this FAQ to the page.")}>
              Add to page
            </Button>
            <Button
              variant="outline-secondary"
              disabled={busy}
              onClick={() => void run(onDismiss, 'Dismissed', "Couldn't dismiss this FAQ.")}
            >
              Dismiss
            </Button>
          </>
        )}
      </div>
      {actionError ? (
        <Alert variant="danger" className="mt-2">
          {actionError}
        </Alert>
      ) : null}
    </li>
  );
}

export function SuggestedFaqSkeleton() {
  return (
    <li data-testid="suggested-faq-skeleton" aria-hidden="true" className="rounded-xl p-4 ring-1 ring-inset ring-slate-200/80">
      <div className="h-4 w-2/3 rounded bg-slate-200" />
      <div className="mt-3 flex gap-2">
        <div className="h-4 w-20 rounded-full bg-slate-100" />
        <div className="h-4 w-16 rounded-full bg-slate-100" />
        <div className="h-4 w-24 rounded-full bg-slate-100" />
      </div>
      <div className="mt-3 h-3 w-full rounded bg-slate-100" />
    </li>
  );
}
